/**
 * BL-2503 — the "No access" wording a role-gated surface shows in place of what it
 * hides no more (ADR 030 — Design Canon, "Role-gated UI is locked, not hidden").
 *
 * Every line is built from the grant vocabulary the route gate enforces
 * (ADR 062 — Access Grants): the area from `RESOURCE_DISPLAY`, the action from
 * `GRANT_DISPLAY`, and the roles from the same role map `rolesHoldGrant` reads. So a
 * line can never name a role the server would refuse. Pure and client-bundle-safe
 * (ADR 010 — Isomorphic Services).
 */
import type { Grant } from './grants';
import { hasPermission, type Permission } from './permissions';
import { GRANT_DISPLAY, RESOURCE_DISPLAY, rolesHoldGrant, type GrantResource } from './role-grants';
import { TENANT_ASSIGNABLE_ROLES, type StaffRole } from './roles';

/** The title of a locked page, tab or panel. */
export const NO_ACCESS_TITLE = 'No access';

/** The line a platform-managed Settings page shows (ADR 031 — Config Ownership § 2). */
export const PLATFORM_MANAGED_LINE = 'Managed by Auctogon platform staff.';

/**
 * The tenant roles that hold any of `permissions`, in display order: for a Settings
 * leaf's one permission, or (BL-2559) a page gated on a permission set with no one
 * grant behind it, such as the clerk console's `clerking.view`. Super Admin is a
 * platform flag, never a tenant role, so it is never named.
 *
 * @param input.permissions - the permissions a surface admits; holding any one opens it.
 * @returns The holding roles.
 */
export function tenantRolesHoldingAnyPermission({ permissions }: { permissions: readonly Permission[] }): StaffRole[] {
  return TENANT_ASSIGNABLE_ROLES.filter((role) => permissions.some((permission) => hasPermission([role], permission)));
}

/**
 * The tenant roles that hold any of `grants`, in display order, read through
 * `rolesHoldGrant`, the predicate the route gate calls, so the copy and the gate
 * cannot disagree.
 *
 * @param input.grants - the grants a surface admits; holding any one opens it.
 * @returns The holding roles.
 */
export function tenantRolesHolding({ grants }: { grants: readonly Grant[] }): StaffRole[] {
  return TENANT_ASSIGNABLE_ROLES.filter((role) =>
    grants.some((grant) => rolesHoldGrant([role], grant)),
  );
}

/**
 * "the Admin role", "the Admin and Accounting roles",
 * "the Admin, Cataloger and Accounting roles".
 */
function roleListPhrase(roles: readonly StaffRole[]): string {
  if (roles.length === 1) return `the ${roles[0]} role`;
  const head = roles.slice(0, -1).join(', ');
  return `the ${head} and ${roles[roles.length - 1]} roles`;
}

/**
 * The one line under a locked page or tab's "No access" title.
 *
 * @param input.area - what the page is, e.g. "Customers" or a Settings leaf label.
 * @param input.roles - the tenant roles that hold it.
 * @returns e.g. "Customers is open to the Admin and Accounting roles. Ask an Admin if you need it."
 */
export function pageNoAccessLine({ area, roles }: { area: string; roles: readonly StaffRole[] }): string {
  // No house role holds it: only Auctogon platform staff can (ADR 031 — Config Ownership).
  if (roles.length === 0) return `${area} is managed by Auctogon platform staff.`;
  return `${area} is open to ${roleListPhrase(roles)}. Ask an Admin if you need it.`;
}

/**
 * A locked page's line, worded from the grants that open it: the area is their one
 * resource's display name, the roles those that hold any of them.
 *
 * @param input.grants - the page's grants, all on one resource.
 * @returns The page line.
 * @throws When `grants` is empty, or spans two resources (no one area to name).
 */
export function grantPageNoAccessLine({ grants }: { grants: readonly Grant[] }): string {
  if (grants.length === 0) throw new Error('grantPageNoAccessLine needs at least one grant');
  const resources = new Set(grants.map((grant) => grant.split('.')[0] as GrantResource));
  if (resources.size !== 1) throw new Error('grantPageNoAccessLine needs grants on one resource');
  const [resource] = resources;
  return pageNoAccessLine({ area: RESOURCE_DISPLAY[resource], roles: tenantRolesHolding({ grants }) });
}

/** A grant's label as the action in a sentence: "View invoices" → "view invoices". */
function actionOf(grant: Grant): string {
  const label = GRANT_DISPLAY[grant];
  return `${label.charAt(0).toLowerCase()}${label.slice(1)}`;
}

/**
 * The one line on a locked card or control: the grant's own label, as the action.
 * A card either of two grants opens names both (BUG-2188, ADR 062 — Access
 * Grants), the shared verb once: "view invoices or settlements".
 *
 * @param input.grant - the grant the card or control needs.
 * @param input.orGrant - a second grant that also opens it, if any.
 * @returns e.g. "Your role can't view invoices. Ask an Admin."
 */
export function controlNoAccessLine({ grant, orGrant }: { grant: Grant; orGrant?: Grant }): string {
  const first = actionOf(grant);
  if (orGrant == null) return `Your role can't ${first}. Ask an Admin.`;
  const second = actionOf(orGrant);
  const verb = first.split(' ')[0];
  const rest = second.startsWith(`${verb} `) ? second.slice(verb.length + 1) : second;
  return `Your role can't ${first} or ${rest}. Ask an Admin.`;
}

/**
 * BUG-2272 — the line on a locked settings row: who can use it. Roles are named in
 * the canonical display order and any string that is not a tenant role (Super
 * Admin, a retired role) is dropped, so the line cannot name a role the server
 * would not recognise.
 *
 * @param input.roles - the roles the row is open to, e.g. a notification catalogue entry's `eligibleRoles`.
 * @returns e.g. "Requires the Admin role." or "Requires the Admin or Accounting role."
 */
export function rolesRequirementLine({ roles }: { roles: readonly string[] }): string {
  const named = TENANT_ASSIGNABLE_ROLES.filter((role) => roles.includes(role));
  if (named.length === 0) return 'Requires elevated access.';
  if (named.length === 1) return `Requires the ${named[0]} role.`;
  return `Requires the ${named.slice(0, -1).join(', ')} or ${named[named.length - 1]} role.`;
}

/** "an Admin", "an Admin or Accounting", "an Admin, Cataloger or Accounting". */
function askRolePhrase(roles: readonly StaffRole[]): string {
  const article = /^[AEIOU]/.test(roles[0] ?? '') ? 'an' : 'a';
  if (roles.length <= 1) return `${article} ${roles[0] ?? 'Admin'}`;
  return `${article} ${roles.slice(0, -1).join(', ')} or ${roles[roles.length - 1]}`;
}

/**
 * BL-2671 — the Net position card's words when the viewer's role can't read one
 * of the two sides (AC 11). The area comes from `RESOURCE_DISPLAY` and the roles
 * from `tenantRolesHolding`, so the line never names a role the gate would refuse.
 *
 * @param input.grant - The read the viewer lacks: `invoice.read` or `settlement.read`.
 * @returns The title in the amount's place, the line under it, and the line in the Apply button's place.
 */
export function netNeedsReadAccessLines({ grant }: { grant: 'invoice.read' | 'settlement.read' }): {
  title: string;
  line: string;
  control: string;
} {
  const [resource, verb] = grant.split('.') as [GrantResource, string];
  const area = RESOURCE_DISPLAY[resource];
  const action = `${verb.charAt(0).toUpperCase()}${verb.slice(1)}`;
  return {
    title: `Needs ${area} access`,
    line: `Ask ${askRolePhrase(tenantRolesHolding({ grants: [grant] }))} for ${area} · ${action} to see the net.`,
    control: `Needs ${area} access to offset`,
  };
}
